import { useCallback, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcTradereportingV1Sec606AReportQueryServiceSec606AReportGet } from '@interxfi/api-client/generated/hooks/ixc-tradereporting-v1-sec606-a-report-query-service-sec606-a-report-get';
import { useIxfReportsV1ReportCommandServiceReportDownload } from '@interxfi/api-client/generated/hooks/ixf-reports-v1-report-command-service-report-download';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import { type IxcTradereportingV1Sec606AReportState, IxcTradereportingV1Sec606AReportStatus } from '@interxfi/types';
import { downloadFromUrl } from '../../../../../../../../../../features/document/utils';
import { getSec606ReportHasFileKeys } from '../../../../../../../../../../features/trade-reporting/sec-606/trade-reporting-sec-606-helpers';
import {
  Sec606ReportEventDrawer,
  Sec606ReportEventSummary,
} from '../../../../../../../../../../features/trade-reporting/sec-606/trade-reporting-sec-606-report-event';
import { getSec606ReportStateListItems } from '../../../../../../../../../../features/trade-reporting/sec-606/trade-reporting-sec-606-report-list-items';
import { getSec606ReportDetailPath } from '../../../../../../../../../../features/trade-reporting/sec-606/trade-reporting-sec-606-report-routing';
import { TradeReportingSec606ReportStatusPill } from '../../../../../../../../../../features/trade-reporting/sec-606/trade-reporting-sec-606-report-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/reporting/sec-606/_sec-606/$reportId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sec-606a-report-${params.reportId}`,
        label: 'SEC 606(a) Report',
        to: getSec606ReportDetailPath(params.reportId),
        suffix: (
          <CopyButton
            content={params.reportId}
            label="Copy SEC 606(a) Report ID"
            tooltip={{ copy: params.reportId, copied: 'Copied SEC 606(a) Report ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('SEC 606(a) Report'),
  component: Sec606aReportDetail,
});

function getListItems(data: IxcTradereportingV1Sec606AReportState | undefined) {
  const listItems = getSec606ReportStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [
            listItems.data.quarter,
            listItems.data.year,
            listItems.data.crd,
            listItems.data.context,
            listItems.data.pdfSubmissionFileKey,
            listItems.data.xmlSubmissionFileKey,
            ...(listItems.data.acceptedFileKey ? [listItems.data.acceptedFileKey] : []),
            ...(listItems.data.rejectedFileKey ? [listItems.data.rejectedFileKey] : []),
            ...(listItems.data.publishUrl ? [listItems.data.publishUrl] : []),
          ],
        },
        ...(listItems.data.errorsAsKeyValuePairs?.length
          ? [
              {
                id: 'submissionErrors',
                title: 'Submission Errors',
                items: listItems.data.errorsAsKeyValuePairs,
              },
            ]
          : []),
      ] satisfies ReadonlyListGroup[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.data.submissionTime,
        listItems.reportId,
        ...(listItems.data.supersededById ? [listItems.data.supersededById] : []),
      ] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(reportId: string, hasFileKeys: boolean, downloadReport: VoidFunction, isDownloading: boolean): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: Sec606ReportEventDrawer,
        props: { request: { reportId } },
      },
    },
    actions: hasFileKeys
      ? [
          {
            id: 'download',
            icon: <DownloadIcon aria-hidden />,
            label: 'Download',
            disabled: isDownloading,
            onClick: downloadReport,
          },
        ]
      : [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load SEC 606(a) report', 'view this SEC 606(a) report');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to download report', 'download this report');

function Sec606aReportDetail() {
  const { reportId } = Route.useParams();
  const request = { reportId } as const;
  const { data, error, isPending } = useIxcTradereportingV1Sec606AReportQueryServiceSec606AReportGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const { mutateAsync: downloadReport, error: downloadError, isPending: isDownloading } = useIxfReportsV1ReportCommandServiceReportDownload();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const handleDownloadReport = useCallback(async () => {
    try {
      const res = await downloadReport({ reportId });

      if (res?.url) {
        downloadFromUrl(res.url);
      }
    } catch {}
  }, [downloadReport, reportId]);

  const listItems = useMemo(() => getListItems(data?.sec606AReport), [data?.sec606AReport]);
  const hasFileKeys = getSec606ReportHasFileKeys(data?.sec606AReport);
  const menuItems = useMemo(
    () => getMenuItems(reportId, hasFileKeys, handleDownloadReport, isDownloading),
    [reportId, hasFileKeys, handleDownloadReport, isDownloading],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <TradeReportingSec606ReportStatusPill isPending={showPending} size="large" status={data?.sec606AReport?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <Sec606ReportEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
